import {
    useScopedMutation,
    useScopedQuery,
    useScopedQueryClient,
} from '@/lib/scopedQuery';
import { api } from './client';

export interface BehaviorType {
    id: string;
    name: string;
    description: string;
    status: string;
}

export interface BehaviorTypeWritePayload {
    name?: string;
    description?: string;
    status?: string;
}

async function fetchBehaviorTypes(): Promise<BehaviorType[]> {
    const res = await api.get<BehaviorType[]>('/behavior-types');
    return res.data;
}

export function useBehaviorTypes() {
    return useScopedQuery({
        queryKey: ['behavior-types'] as const,
        queryFn: fetchBehaviorTypes,
        staleTime: 60_000,
    });
}

export function useCreateBehaviorType() {
    const qc = useScopedQueryClient();
    return useScopedMutation({
        mutationFn: async (payload: BehaviorTypeWritePayload) => {
            const res = await api.post<BehaviorType>('/behavior-types', payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['behavior-types'] }),
    });
}

export function useUpdateBehaviorType() {
    const qc = useScopedQueryClient();
    return useScopedMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: BehaviorTypeWritePayload }) => {
            const res = await api.patch<BehaviorType>(`/behavior-types/${id}`, payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['behavior-types'] }),
    });
}

export function useDeleteBehaviorType() {
    const qc = useScopedQueryClient();
    return useScopedMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/behavior-types/${id}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['behavior-types'] }),
    });
}
